import { useState, useEffect, useCallback, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { GetSearchData } from "../CRUDPage/FetchData";
import { getToday } from "./GetToday";
import ListODOSImages from "./ListODOSImages";
import { drugTransList, chinese2Simp, drugColor } from "../../Variables/drugInfo";

const today = getToday();
const highTestContent = {AL:["AL", "ACD", "LT", "CCT", "WTW", "Pupil", "Kappa", "angle"], G:["ART", "CD"], VF:["VFI", "MD10", "MD24", "MD24c", "MD30"], M:["CAT"]}
const highTestStyle = {AL:{color:"#f41919", offset:8}, G:{color:"#387bf8", offset:-5}, VF:{color:"#72ff98", offset:-25}, M:{color:"#d272ff", offset:-40}};
// let className="bg-[#ffe415] bg-[#a33af9] bg-[#f89b0f] bg-[#0ff8c9] bg-[#f53871] bg-[#0f70f8] bg-[#2eef1d] bg-[#f538f5]"
// let className="bg-[#387bf8]"

function RecordChart({patientData}) {
    // console.log(drugColor)
    const [fetchedData, setFetchedData] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [drugData, setDrugData] = useState([]);
    const [overallStartEnd, setOverallStartEnd] = useState({start: undefined, end:undefined})
    const [filterStartEnd, setFilterStartEnd] = useState({start: undefined, end:undefined, subChart: null})   
    const lineRef = useRef(null);
    const timeLineRef = useRef(null);
    const isZooming = useRef(false);

    const zoomChart = useCallback((passStartEnd, caller) => {
        const localStart = passStartEnd.start && !isNaN(passStartEnd.start) ? new Date(passStartEnd.start).getTime() : getDateForAxis(overallStartEnd.start, 'start');
        const localEnd = passStartEnd.end && !isNaN(passStartEnd.end) ? new Date(passStartEnd.end).getTime() : getDateForAxis(overallStartEnd.end, 'end');
        // console.log(passStartEnd, localStart, localEnd, caller)
        if(!isZooming.current){ 
            isZooming.current = true;
            if(lineRef.current && caller!=='test'){
                lineRef.current.chart.zoomX(localStart, localEnd);
            }
            if(timeLineRef.current && caller!=='drug'){
                timeLineRef.current.chart.zoomX(localStart, localEnd);
            }
            isZooming.current = false;
        }
    }, [isZooming])

    const resetAxis = () => {
        if(lineRef.current) lineRef.current.chart.resetSeries();
        if(timeLineRef.current) timeLineRef.current.chart.resetSeries();
    }

    const [props, setProps] = useState({
        lineOptions: {
            lineSeries: [],
            chart: {
                id: 'test',
                // group: 'recordChart',
                type: 'line',
                height: 160,
                events:{
                    zoomed: (chartContext, {xaxis}) => {
                        zoomChart({start: xaxis.min, end: xaxis.max}, 'test');
                    },
                    // scrolled: (chartContext, {xaxis}) => {
                    //     scroll of line chart is not working
                    //     console.log("scroll", xaxis)
                    // },
                    resetZoom: resetAxis()
                },
            },
            colors: ['#50f353', '#f35050'],
            xaxis: {
                type: 'datetime',
                tickPlacement: 'on',
                labels: {
                    trim: true,
                    style: {
                        fontSize: "20px",
                    },
                    datetimeFormatter: {
                        year: 'yyyy',
                        month: 'MM月dd日',
                        day: 'MM月dd日',
                    },
                }
            },
            dataLabels: {
                enabled: true,
                style: {
                    fontSize: "16px",
                }
            },
            legend: {
                fontSize: "18px",
            },
            tooltip: {
                style: {
                    fontSize: '18px'
                },
                x: {
                    format: 'yyyy/MM/dd'
                }
            },
            yaxis: {
                min: 15,
                max: 30,
                stepSize: 1,
                // labels: {
                //     style: {
                //         fontSize: '12px',
                //     },
                // }
            }
        },
        timeLineOptions: {
            timeLineSeries: [],
            chart: {
                id:"drug",
                // group: 'recordChart',
                height: 450,
                type: 'rangeBar',
                events:{
                    zoomed: (chartContext, {xaxis}) => {
                        zoomChart({start: xaxis.min, end: xaxis.max}, 'drug');
                    },
                    scrolled: (chartContext, {xaxis}) => {
                        zoomChart({start: xaxis.min, end: xaxis.max}, 'drug');
                    },
                    resetZoom: resetAxis()
                },
            },
            plotOptions: {
                bar: {
                    horizontal: true,
                    barHeight: '60%'
                }
            },
            xaxis: {
                type: 'datetime',
                labels: {
                    // trim: true,
                    rotate: -15,
                    rotateAlways: true,
                    offsetX: 100,
                    offsetY: -0,
                    style: {
                        fontSize: "20px",
                    },
                    datetimeFormatter: {
                        year: 'yyyy',
                        month: 'MM月dd日',
                        day: 'MM月dd日',
                    },
                }
            },
            yaxis: {
                labels:{
                    show: true,
                    formatter: (val) => {
                        return chinese2Simp[val] ? chinese2Simp[val] : val;
                        // return simpDrug[val]
                    }
                }
            },
            fill: {
                type: 'solid',
                opacity: 0.6
            },
            dataLabels: {
                enabled: false, // timeline chart datalabel switch
                formatter: function(val, opts) {
                    var label = opts.w.globals.labels[opts.dataPointIndex]
                    return label
                },
                style: {
                    fontSize: "16px",
                }
            },
            tooltip: {
                style: {
                    fontSize: '18px'
                },
                x: {
                    format: 'yyyy/MM/dd'
                }
            }
        },                
    });

    // functions for chart
    const getDateForAxis = (date, startOrEnd) => {
        let finalDate = new Date(date);
        if(startOrEnd==='start')finalDate.setDate(new Date(date).getDate()-2);
        if(startOrEnd==='end')finalDate.setDate(new Date(date).getDate()+2);
        return finalDate.getTime();
    }

    const setAxisMinMax = useCallback((passStartEnd) => {
        let localStart, localEnd;
        if(passStartEnd.start===undefined || isNaN(passStartEnd.start.getTime())){
            if(overallStartEnd.start===undefined){
                localStart=undefined;
            }
            else{
                localStart = getDateForAxis(overallStartEnd.start, 'start');
            }
        }
        else{
            localStart = getDateForAxis(passStartEnd.start, 'start');
        }

        if(passStartEnd.end===undefined || isNaN(passStartEnd.end.getTime())){
            if(overallStartEnd.end===undefined){
                localEnd=undefined;
            }
            else{
                localEnd = getDateForAxis(overallStartEnd.end, 'end')
            }
        }
        else{
            localEnd = getDateForAxis(passStartEnd.end, 'end')
        }

        setProps(prevState => ({
            ...prevState,

            lineOptions:{
                ...prevState.lineOptions,
                xaxis:{
                    ...prevState.lineOptions.xaxis,
                    min:localStart,
                    max:localEnd
                },
            },

            timeLineOptions:{
                ...prevState.timeLineOptions,
                xaxis:{
                    ...prevState.timeLineOptions.xaxis,
                    min:localStart,
                    max:localEnd
                }
            }
        }))
        console.log("finished")
    }, [overallStartEnd, fetchedData, chartData] )

    // function
    const dealChartData = (chartRawData, targetData) => {
        let returnObject = [
            {
                name: targetData+"_OD",
                data: []
            },
            {
                name: targetData+"_OS",
                data: []
            }
        ]

        let localStart = overallStartEnd.start;
        let localEnd = overallStartEnd.end;
        chartRawData.forEach((record) => {
            let data_OD = targetData==="IOP" ? (record.iop_records.length>0 ? record.iop_records[0].IOP_OD : null) : record[targetData+"_OD"];
            let data_OS = targetData==="IOP" ? (record.iop_records.length>0 ? record.iop_records[0].IOP_OS : null) : record[targetData+"_OS"];
            let testDate = new Date(record.test_date);
            returnObject[0].data.push({
                x: testDate.getTime(),
                y: data_OD
            });
            returnObject[1].data.push({
                x: testDate.getTime(),
                y: data_OS
            });
            if(localStart===undefined || testDate < localStart) localStart = testDate;
            if(localEnd===undefined || testDate > localEnd) localEnd = testDate;
        })
        setOverallStartEnd({start: localStart, end: localEnd});
        return returnObject;
    }

    const dealDrugData = (drugRawData) => {
        let localStart = overallStartEnd.start;
        let localEnd = overallStartEnd.end;

        let returnArray = drugRawData.map((record) => {
            let start_date = new Date(record.start_date);
            let end_date = new Date(record.start_date);
            end_date.setDate(start_date.getDate() + record.drug_due_day);
            if(localStart===undefined || start_date < localStart) localStart = start_date;
            if(localEnd===undefined || end_date > localEnd) localEnd = end_date;
            return {
                x: drugTransList[record.drug] ? drugTransList[record.drug] : record.drug,
                y: [start_date.getTime(), end_date.getTime()],
                fillColor: drugColor[record.drug] ? drugColor[record.drug][0] : undefined,
                fill: {
                    type: 'gradient',
                    gradient: {
                        gradientToColors: drugColor[record.drug] ? 
                        (Array.isArray(drugColor[record.drug]) && drugColor[record.drug][1] ? drugColor[record.drug].slice(1) : [drugColor[record.drug][0]]) // optional, if not defined - uses the shades of same color in series
                         : ["#0090FF"]
                    }
                }
            }
        })
        setOverallStartEnd({start: localStart, end: localEnd});
        return [{data:returnArray}];
    }

    const getHigherTest = async(chartRawData) => {
        let annotation = [];
        await chartRawData.forEach((record, index) => {
            Object.entries(highTestContent).forEach(([show, check]) => {
                let hasHighTest = false;
                for(const test of check){
                    if(record[`${test}_OD`] || record[`${test}_OS`]){
                        hasHighTest = true;
                        break;
                    }
                }
                if(hasHighTest){
                    annotation.push(
                        {
                            x: chartData[0].data[index].x,
                            stroke: {
                                width: 0
                            },
                            label: {
                                text: show,
                                borderColor: highTestStyle[show].color,
                                style:{
                                    fontSize: 12,
                                    background: `${highTestStyle[show].color}80`
                                },
                                offsetY: highTestStyle[show].offset
                            }
                        }
                    )
                }
            })
        })
        console.log("annotation:",annotation);
        return annotation;
    }

    //fetch data
    let temEye = GetSearchData("eye_record/"+patientData.client_data_record_id);
    let temDrug = GetSearchData("drug_record/"+patientData.client_data_record_id);
    // console.log("return:",temEye)

    useEffect(() => {
        if(Array.isArray(temEye) && Array.isArray(temDrug)){
            try{
                let temData = {
                    chartRawData: temEye,
                    drugRawData: temDrug
                }
                setFetchedData(temData)

            }
            catch(error){
                console.log("error in setFetchedData:",error)
            }
        }
    }, [temEye, temDrug])

    useEffect(() => {
        try{
            console.log("fetch:", fetchedData)
            let temChartData = dealChartData(fetchedData.chartRawData, "IOP");
            let temDrugData = dealDrugData(fetchedData.drugRawData);
            temDrugData[0].data.sort((a,b) => {return a.x.localeCompare(b.x)})
            setChartData(temChartData);
            setDrugData(temDrugData);
        }
        catch(error){
            console.log("error in setProps:",error)
        }
    }, [fetchedData])

    useEffect(() => {
        //move annotations here
        if(Array.isArray(temEye) && Array.isArray(temDrug)){
            try{
                console.log("chart:", chartData)
                console.log("drug:", drugData)

                //annotation
                getHigherTest(fetchedData.chartRawData)
                .then(annotation => {
                    setProps(prevState => ({
                        ...prevState,
                        lineOptions: {
                            ...prevState.lineOptions,
                            lineSeries: chartData,
                            annotations: {
                                xaxis: annotation,
                            }
                        },
                        timeLineOptions: {
                            ...prevState.timeLineOptions,
                            timeLineSeries: drugData
                        }
                    }))
                    console.log(props)
                });
            }
            catch(error){
                console.log("error in setProps:",error)
            }
        }
    }, [chartData, drugData])

    useEffect(() => {
        try{
            setAxisMinMax(overallStartEnd);
        }
        catch(error){
            console.log("error in setStartEnd:",error)
        }
    }, [overallStartEnd, setAxisMinMax])

    return(<>
        {(!Array.isArray(temEye) && !Array.isArray(temDrug)) ? "loading..." : (
            <div className="text-xl">
                <select onChange={(event) => event.target.value!=='' ? (setChartData(dealChartData(chartData, event.target.value))) : ('')}>
                    <option key="IOP">{"IOP"}</option>
                    {/* <option key="VA">{"VA"}</option> */}
                </select>
                <div>
                    篩選日期: <input type="date" max={today} onChange={(event) => {setFilterStartEnd(prevState => ({...prevState, start: new Date(event.target.value)}))}}></input>
                     ~ <input type="date" max={today} onChange={(event) => {setFilterStartEnd(prevState => ({...prevState, end: new Date(event.target.value)}))}}></input>
                <button onClick={() => {zoomChart(filterStartEnd)}} className="p-4">篩選</button>
                <button onClick={() => {resetAxis()}} className="p-4">還原</button>
                </div>
                <div id="wrapper">
                <div id="chart-line">
                    <div>*折線圖目前無法拖動</div>
                    <div className="ps-2.5"><ReactApexChart ref={lineRef} options={props.lineOptions} series={props.lineOptions.lineSeries} type="line" height={250}/></div>
                    <div className=""><ReactApexChart ref={timeLineRef} options={props.timeLineOptions} series={props.timeLineOptions.timeLineSeries} type="rangeBar" height={250}></ReactApexChart></div>
                </div>
                </div>
                <ListODOSImages records={temEye} client_id={patientData.client_data_record_id}></ListODOSImages>
            </div>
        )}
    </>)
}

export default RecordChart;
